import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
    maxWidth: '450px',
    width: '100%',
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    textAlign: 'center',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  width: '72px',
  height: '72px',
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.primary.main}15)`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 6px 16px rgba(0, 82, 204, 0.08)',
  border: `1px solid ${theme.palette.primary.main}20`,
}));

const Title = styled(DialogTitle)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '22px',
  fontWeight: 600,
  padding: theme.spacing(1),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: theme.spacing(1, 3),
  minWidth: '120px',
  textTransform: 'none',
  fontSize: '16px',
  fontWeight: 600,
}));

/**
 * @param {Object} props
 * @param {boolean} props.open 
 * @param {Function} props.onClose 
 * @param {Function} props.onContinue 
 * @param {Function} props.onCancel 
 * @param {boolean} props.isProcessing 
 * @param {string} props.actionType 
 */
const SaveDraftConfirmationDialog = ({ 
  open, 
  onClose, 
  onContinue, 
  onCancel,
  isProcessing = false,
  actionType = 'navigation', // 'navigation' or 'logout'
}) => {
  /**
   * @returns {string}
   */
  const getTitle = () => {
    return actionType === 'logout' 
      ? 'Save Application Before Logout?' 
      : 'Save Application as Draft?';
  };
  
  /**
   * @returns {string}
   */
  const getMessage = () => {
    return actionType === 'logout'
      ? 'You are about to log out with an incomplete loan application. Would you like to save your progress as a draft?'
      : 'You are leaving the application form with unsaved changes. Would you like to save your progress as a draft?';
  };

  return (
    <StyledDialog
      open={open}
      onClose={isProcessing ? null : onClose}
      aria-labelledby="save-draft-dialog-title"
    >
      <IconContainer>
        <SaveIcon color="primary" sx={{ fontSize: 36 }} />
      </IconContainer>
      
      <Title id="save-draft-dialog-title">
        {getTitle()}
      </Title>
      
      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {getMessage()}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', gap: 2, px: 3, pb: 3 }}>
        <StyledButton 
          onClick={onCancel}
          variant="outlined" 
          color="primary"
          disabled={isProcessing}
        >
          Don't Save
        </StyledButton>
        
        <StyledButton 
          onClick={onContinue}
          variant="contained" 
          color="primary"
          disabled={isProcessing}
          startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isProcessing ? 'Saving...' : 'Save Draft'}
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default SaveDraftConfirmationDialog; 